import styled, { css } from 'styled-components'

// Main container
export const AmenitiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const AmenitiesContentContainer = styled.div`
  margin: 0 ${({ theme }) => theme.spacing.large};
`

// Header section with statistics and score
export const SectionHeaderScore = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const StatsCards = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`

export const StatCard = styled.div<{
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'primary'
}>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 24px 32px;
  text-align: center;
  transition: all 0.2s;
  min-width: 140px;
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  ${({ variant, theme }) => {
    switch (variant) {
      case 'success':
        return css`
          border-color: ${theme.colors.success};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.success}15;
          }
        `
      case 'warning':
        return css`
          border-color: ${theme.colors.warning};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.warning}15;
          }
        `
      case 'danger':
        return css`
          border-color: ${theme.colors.danger};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.danger}15;
          }
        `
      case 'primary':
        return css`
          border-color: ${theme.colors.primary};
          &:hover {
            box-shadow: 0 2px 8px ${theme.colors.primary}15;
          }
        `
      default:
        return ''
    }
  }}

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    padding: 20px 16px;
  }
`

export const StatValue = styled.div<{
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'primary'
}>`
  font-size: 32px;
  font-weight: 600;
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.success
      case 'warning':
        return theme.colors.warning
      case 'danger':
        return theme.colors.danger
      case 'primary':
        return theme.colors.primary
      default:
        return theme.colors.text.primary
    }
  }};
  line-height: 1;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`

export const StatLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`

// Score display component
export const ScoreDisplayComponent = styled.div<{
  variant?: 'success' | 'warning' | 'danger' | 'primary'
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: all 0.2s;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

export const ScoreDisplay = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`

export const ScoreValue = styled.span<{
  variant?: 'success' | 'warning' | 'danger' | 'primary'
}>`
  font-size: 32px;
  font-weight: 600;
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.success
      case 'warning':
        return theme.colors.warning
      case 'danger':
        return theme.colors.danger
      case 'primary':
        return theme.colors.primary
      default:
        return theme.colors.text.primary
    }
  }};

  @media (max-width: 768px) {
    font-size: 28px;
  }
`

export const ScoreMax = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

export const ScoreLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

// Category card
export const AmenityCategoryCard = styled.div<{ expanded?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 20px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`

export const AmenityCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  cursor: pointer;
`

export const AmenityCategoryInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`

export const AmenityCategoryIcon = styled.div<{ color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: ${({ color }) =>
    color ? `${color}1a` : 'rgba(0, 173, 230, 0.1)'};
  color: ${({ color }) => color || '#00ade6'};
  flex-shrink: 0;
`

export const AmenityCategoryDetails = styled.div`
  flex: 1;
  min-width: 0;
`

export const AmenityCategoryTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const AmenitySubcategories = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`

export const AmenitySubcategory = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const AmenityCategoryScore = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  @media (max-width: 768px) {
    margin-top: 12px;
    align-items: flex-start;
    text-align: left;
  }
`

export const AmenityScoreValue = styled.span<{
  variant?: 'low' | 'medium' | 'high' | 'excellent'
}>`
  font-size: 32px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme, variant }) => {
    switch (variant) {
      case 'excellent':
        return theme.colors.success
      case 'high':
        return theme.colors.success
      case 'medium':
        return theme.colors.warning
      case 'low':
        return theme.colors.danger
      default:
        return theme.colors.text.primary
    }
  }};
`

export const AmenityScoreMax = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

// Amenity list
export const AmenityList = styled.div<{ $expanded?: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '600px' : '200px')};
  overflow-y: ${({ $expanded }) => ($expanded ? 'auto' : 'hidden')};
  transition: all 0.3s ease-in-out;
  padding: ${({ $expanded }) => ($expanded ? '16px 24px' : '8px 24px')};
  border-left: ${({ $expanded }) =>
    $expanded ? '3px solid #00ade6' : '3px solid transparent'};
  background-color: ${({ $expanded }) =>
    $expanded ? '#f8fffe' : 'transparent'};
`

export const AmenityItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  gap: 12px;

  &:last-child {
    border-bottom: none;
  }
`

export const AmenityItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const AmenityItemName = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const AmenityItemType = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const AmenityItemDistance = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
`

export const AmenityShowMore = styled.button`
  display: block;
  margin-top: 12px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    text-decoration: underline;
  }
`

// Expand/collapse arrow
export const AmenityExpandArrow = styled.div<{ $expanded?: boolean }>`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  transition: transform 0.3s ease;
  margin-left: 12px;
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`

// Loading and error states
export const AmenitiesLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  gap: 16px;
`

export const AmenitiesErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  gap: 16px;
  text-align: center;
`

export const ErrorTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`

export const AmenitiesErrorMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 24px;
  max-width: 400px;
`

// Data sources
export const DataSources = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: 16px;

  @media (max-width: 768px) {
    font-size: 11px;
    gap: 6px;
  }
`

export const AmenitiesSourceLabel = styled.span`
  font-weight: 500;
`

export const SourceList = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`
